'use client';

import { DatePicker } from '@koda/koda-components/molecules/DatePicker';
import ModalDialogue from '@koda/koda-components/molecules/ModalDialogue';
import NumberField from '@koda/koda-components/molecules/NumberField';
import dayjs from 'dayjs';
import { useTranslations } from 'next-intl';
import { ReactElement } from 'react';
import { LicenseTypes } from '../../../types/licenses';
import { useLicenseBusiness } from '../hooks/useLicenseBusiness';
import { Past5YearsFuture2Years } from './consts/dateIntervals';
import {
  createShopAndServiceIndustryDefaults,
  SHOP_AND_SERVICE_INDUSTRY_CONTENT_PATH,
  shopAndServiceIndustrySchema,
} from './shopAndServiceIndustry.Schema';

function ShopAndServiceIndustryComponent(): ReactElement {
  const licenseCode = LicenseTypes.ShopAndServiceIndustry;

  const { values, setField, getError, costCalculated, showErrors } = useLicenseBusiness(licenseCode, {
    schema: shopAndServiceIndustrySchema,
    defaults: createShopAndServiceIndustryDefaults,
  });

  const t = useTranslations(SHOP_AND_SERVICE_INDUSTRY_CONTENT_PATH);

  const handleContactUs = () => {
    // Open contact page or handle contact action
    const emailSubject = t('venueSizeExceededModal.emailSubject');
    const emailBodyKey = values.startDate
      ? 'venueSizeExceededModal.emailBodyWithDate'
      : 'venueSizeExceededModal.emailBodyNoDate';
    const emailBodyParams: Record<string, string | number | Date> = values.startDate
      ? { area: values.area, startDate: values.startDate }
      : { area: values.area };
    const emailBody = t(emailBodyKey, emailBodyParams);

    window.location.href = `mailto:kunde@koda.dk?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
  };

  return (
    <>
      <DatePicker
        label={t('startDate.label')}
        name="startDate"
        placeholder={t('startDate.placeholder')}
        disabled={costCalculated}
        value={values.startDate && values.startDate !== '' ? new Date(values.startDate) : undefined}
        onChange={date => setField('startDate', date ? dayjs(date).format('YYYY-MM-DD') : '')}
        error={values.startDate || showErrors ? getError('startDate') : undefined}
        validated={!!values.startDate && !getError('startDate')}
        initialVisibleValue={new Date()}
        interval={Past5YearsFuture2Years}
      />
      <NumberField
        label={t('venueSize.label')}
        name="area"
        placeholder={t('venueSize.placeholder')}
        required
        styleVariant="border"
        disabled={costCalculated}
        value={values.area}
        onChange={e => setField('area', parseInt(e.target.value))}
        helpText={t('venueSize.helpText')}
        error={values.area || showErrors ? getError('area') : undefined}
        validated={!!values.area && !getError('area')}
        formatThousands
        min={1}
        sign="positive-only"
      />
      <ModalDialogue
        title={t('venueSizeExceededModal.title')}
        description={t('venueSizeExceededModal.description')}
        type="info"
        primaryText={t('venueSizeExceededModal.contactUsButton')}
        cancelText={t('venueSizeExceededModal.correctInfoButton')}
        open={typeof values.area === 'number' && values.area > 3000}
        onPrimaryAction={handleContactUs}
        isClosable={false}
        onCancelAction={() => setField('area', undefined as unknown as number)}
      />
    </>
  );
}

// Export with expected name; keeps prop contract but avoids unused variable lint issues
export default ShopAndServiceIndustryComponent;
